import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { SidebarStateService } from '@core/services/sidebar-state.service';
import { LoggerService } from '@core/services/logger.service';
import { ModalSurfaceRegistry } from '@core/services/modal-surface-registry.service';
import {
  CONTENT_REGION_SCOPE,
  MENU_PANEL_SCOPE,
  MENU_ROOT_SCOPE,
  MenuFocusService,
  menuKeyPathSegments
} from '@core/services/menu-focus.service';
import { LocalizedModuleDto, LocalizedSubmoduleDto } from '@core/models/user.model';
import { MenuItem, findByKey, findOpenableChild, isModuleShortcutLabel, menuItemKey } from '@core/models/menu.model';
import { backendIconToLucide, backendRouteToAngular, getDefaultRouteForModuleName, opensAsOverlay, OverlaySubmoduleKey, SUBMODULE_KEY_CADASTRO_CLINICAS, SUBMODULE_KEY_CADASTRO_FILAS_COLETA, SUBMODULE_KEY_CADASTRO_PACIENTE, SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO, SUBMODULE_KEY_CADASTRO_SOLICITANTES, SUBMODULE_KEY_CADASTRO_TABELA_PRECO, SUBMODULE_KEY_ESTABELECIMENTO_SAUDE, SUBMODULE_KEY_TO_PERMISSION, SUBMODULE_KEY_VACINAS_CONFIGURACAO } from '@core/utilities/backend-menu.util';
import { HealthFacilitiesModalService } from '@app/features/health-facilities/services/health-facilities-modal.service';
import { CollectionQueuesModalService } from '@app/features/collection-queues/services/collection-queues-modal.service';
import { VaccinesConfigModalService } from '@app/features/vaccines-config/services/vaccines-config-modal.service';
import { LaboratorySectorsModalService } from '@app/features/laboratory-sectors/services/laboratory-sectors-modal.service';
import { SolicitantesModalService } from '@app/features/solicitantes/services/solicitantes-modal.service';
import { PriceTablesModalService } from '@app/features/price-tables/services/price-tables-modal.service';
import { ClinicsModalService } from '@app/features/clinics/services/clinics-modal.service';
import { PatientRegistryModalService } from '@app/features/patient/services/patient-registry-modal.service';
import { UserAccessService } from '@core/services/user-access.service';
import { resolveExecutableModuleLaunchUrl, isRemovedMenuModule } from '@core/constants/executable-modules.constants';
import {
  resolveExternalLaunchUrlChain,
} from '@core/utilities/external-launch.util';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { NotificationService } from '@core/services/notification.service';
import { I18nService } from '@core/services/i18n.service';
import { AgentPrintService } from '@app/core/services/agent-print.service';
import { LoadingService } from '@app/core/services/loading.service';

// Re-export to keep callers that imported MenuItem from the sidebar working.
// To be removed once every import points to `@core/models/menu.model`.
export { MenuItem } from '@core/models/menu.model';

/**
 * A cascade opening parked while the sidebar finishes expanding.
 *
 * The two facts travel together because they answer two different questions
 * about the same wait, and reading either off the other is what let each of
 * them go wrong on its own. `key` is what the branch is: the tree may be
 * rebuilt during the expansion, and an opening whose branch did not survive it
 * has nothing left to put on screen. `fromKeyboard` is whose it is: only the
 * keyboard's opening makes `Escape` the menu's press to answer, since hover
 * parks one through the very same helper.
 */
interface ParkedOpening {
  /** Stable key of the root item whose cascade is waiting. */
  readonly key: string;
  /** Whether a key press parked it — `false` for hover and click. */
  readonly fromKeyboard: boolean;
}

/** A pointer position in viewport coordinates. */
interface PointerPoint {
  readonly x: number;
  readonly y: number;
}

/** Where `event` was fired. */
function pointOf(event: MouseEvent): PointerPoint {
  return { x: event.clientX, y: event.clientY };
}

/** Whether `event` was fired at exactly `point` (no pointer movement in between). */
function samePoint(event: MouseEvent, point: PointerPoint): boolean {
  return event.clientX === point.x && event.clientY === point.y;
}

/**
 * Lateral sidebar. Renders only the root items; submenus (at any depth) are
 * shown by the recursive <code>app-nav-flyout</code> component, which opens
 * sideways as a cascade instead of the previous inline accordion.
 */
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  private _isOpen = true;
  /**
   * Sidebar expanded state. When the parent toggles this externally (header
   * hamburger button), an open flyout would otherwise stay orphaned on screen
   * because the document:click handler explicitly excludes the hamburger to
   * avoid fighting with the header's own toggle. The setter closes the flyout
   * on every external state change so the cascade can never outlive the
   * sidebar it was anchored to.
   */
  @Input() set isOpen(value: boolean) {
    const changed = this._isOpen !== value;
    this._isOpen = value;
    if (changed) {
      // Any change in isOpen kicks off a CSS `width` transition on `.sidebar`.
      // Latch the flag so `deferUntilSidebarExpanded` waits for the real
      // `transitionend` regardless of who initiated the toggle (TASK-59001
      // follow-up — the previous fix only deferred when *this* component
      // emitted requestSidebarOpen, leaving the hamburger / hover-on-sidebar
      // path racing the flyout against a still-moving anchor).
      this.sidebarTransitioning = true;
      if (!value) this.layoutShiftGuard = null;
      if (this.openItem !== null) {
        this.closeFlyout();
      }
    }
  }
  get isOpen(): boolean { return this._isOpen; }

  /** Request to expand the sidebar (e.g. hover/click on an item while collapsed). */
  @Output() requestSidebarOpen = new EventEmitter<void>();
  /** Request to collapse the sidebar (e.g. leaf activation). */
  @Output() requestSidebarClose = new EventEmitter<void>();

  menuItems: MenuItem[] = [];
  menuLoading = true;

  /** Root item whose flyout is currently open (null = none). */
  openItem: MenuItem | null = null;
  /** Anchor element (root button) used by the flyout for positioning. */
  openAnchor: HTMLElement | null = null;
  /** IDs/labels of items in the active route path, for highlighting the chain. */
  activePath: ReadonlySet<string | number> = new Set();
  /** Current URL without query string — propagated to the flyout to highlight the active leaf. */
  currentUrl: string = '';

  /** Hover delay before opening the flyout, aligned with desktop menu standards. */
  private static readonly HOVER_DELAY_MS = 100;
  /**
   * Hard cap for `deferUntilSidebarExpanded` — used when the browser never
   * fires `transitionend` (element detached, transition interrupted, or
   * background-tab throttling). ~2× the 0.28s CSS `width` transition on
   * `.sidebar` gives slow machines room without making the flyout feel
   * unresponsive when the listener itself drops on the floor.
   */
  private static readonly SIDEBAR_TRANSITION_SAFETY_MS = 600;
  /**
   * Safety net for the hover-suppression after a leaf is selected. Suppression
   * is normally cleared when the user leaves the sidebar (mouseleave); this
   * timer only kicks in if the cursor stays parked over the sidebar for a long time.
   */
  private static readonly HOVER_SUPPRESS_MS = 3000;
  /** Pending hover timer (cancelled on mouseleave). */
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * When true, hovers are ignored (because we just closed via leaf selection) —
   * prevents the loop "close → cursor over item → reopen 100ms later".
   */
  private hoverSuppressed = false;
  /** Timer that clears `hoverSuppressed` after the grace period. */
  private hoverSuppressTimer: ReturnType<typeof setTimeout> | null = null;

  /** Last known pointer position over the bar. */
  private lastPointer: PointerPoint | null = null;
  /**
   * The cursor's rest point while the guard is armed (from the collapsed bar's expand request on).
   * Boundary events at this point come from rows growing under it, not from the user (CG-FRONT-026).
   */
  private layoutShiftGuard: PointerPoint | null = null;

  /**
   * True while a pointer press is what is putting the focus somewhere.
   *
   * The browser focuses the element under the cursor as the DEFAULT ACTION of
   * `mousedown`, which runs after the listeners of that same press — so a
   * landing the mouse produced always arrives with this latched, and a landing
   * `Tab` or a `focus()` produced never does. That is the whole distinction:
   * asked of the gesture, which is the only thing that knows, instead of
   * inferred afterwards from where the focus ended up.
   *
   * Released on a timer rather than on `mouseup` or `click`: a press that ends
   * outside the document — dragged off the window, interrupted by a context
   * menu — delivers neither, and a latch stuck on would silence the keyboard
   * for the rest of the session. One macrotask is exactly the reach the press
   * needs, since its default action is delivered inside the press's own task.
   */
  private focusLandingIsAPointerPress = false;
  /** Handle releasing the latch above (cleared in `ngOnDestroy`). */
  private pointerPressTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Root item whose cascade the KEYBOARD opened — `null` when the panel on
   * screen is the mouse's, or when nothing is open.
   *
   * Held as the item rather than as a boolean of its own because "the keyboard
   * opened what is on screen" is a fact about the CURRENT cascade: a flag
   * needs a reset point at every place `openItem` changes, and two of them
   * (`onRootMouseEnter`'s open and `toggleFlyout`) never went through
   * `closeFlyout()`. Derived from `openItem`, a stale value is impossible by
   * construction — the third forgotten reset point cannot exist.
   */
  private keyboardOpenedItem: MenuItem | null = null;

  /**
   * When true, opening the flyout focuses the first item of the child component.
   *
   * Compared by stable key: `applyMenu` re-points `openItem` at the rebuilt
   * tree, so an identity check would go false on every re-render and a cascade
   * the keyboard opened would start reading as the mouse's — with nothing
   * having happened (cause C-04). That axis — key versus reference — is the one
   * a test discriminates, and the AC12/C-04 re-render case is where it lives.
   *
   * **The pairing itself is defence in depth, and it is not redundant: do not
   * "simplify" it to the two null checks.** No path leaves the record and the
   * cascade on screen naming two DIFFERENT items — every mouse gesture says so
   * before it opens (`declareTheCascadeTheMouses`), the keyboard writes the
   * record with the opening it describes, `closeFlyout` clears it, and
   * `rebindOpenCascade` only ever swaps `openItem` for another object of the
   * SAME key. So the comparison cannot be caught failing, and a mutation that
   * drops it leaves the suite green. It stays because the invariant it asserts
   * is maintained by hand at every place either field is written, and the day
   * one of them forgets, this is what refuses to describe a cascade nobody
   * asked for.
   *
   * It is also NOT what closes AC8 at the root: a re-opening by hover or click
   * re-arms the panel's opening latch whatever this getter says, and what
   * stops it is the mouse declaring the cascade its own before it opens.
   */
  get flyoutAutofocus(): boolean {
    return this.keyboardOpenedItem !== null
      && this.openItem !== null
      && menuItemKey(this.keyboardOpenedItem) === menuItemKey(this.openItem);
  }

  /** References to the root links for keyboard navigation (arrows, Home/End). */
  @ViewChildren('rootLink') rootLinks!: QueryList<ElementRef<HTMLElement>>;
  /**
   * Reference to the `<aside class="sidebar">` element. Used to wait for its
   * `width` CSS transition to complete before opening the flyout, so the
   * anchor's `getBoundingClientRect()` is measured in the fully expanded
   * position (TASK-59001).
   */
  @ViewChild('sidebarEl', { static: false }) sidebarEl?: ElementRef<HTMLElement>;

  /**
   * Cleanup callback for any pending "wait for sidebar transition" action.
   * Called by `cancelHoverTimer` and `ngOnDestroy` so the deferred work is
   * dropped if the user navigates away or hovers a different item before
   * the transition finishes.
   */
  private pendingExpandCleanup: (() => void) | null = null;

  /**
   * The cascade opening parked while the sidebar expands — `null` when none is.
   *
   * Recorded at defer time because `pendingExpandCleanup` alone cannot answer
   * either of the questions asked of it: which branch is waiting, and whose
   * gesture parked it. Every path — key, hover, click — parks through the same
   * helper.
   */
  private pendingOpen: ParkedOpening | null = null;

  /**
   * True while the sidebar's `width` CSS transition is in progress.
   * Latched in the `isOpen` setter (any external/internal toggle starts a
   * transition) and cleared by the persistent `transitionend` listener on
   * the `<aside>` element. Used as the gate by `deferUntilSidebarExpanded`
   * so any concurrent click/hover waits until the anchor stops moving,
   * regardless of which path triggered the toggle (TASK-59001).
   */
  private sidebarTransitioning = false;

  /** Cleanup for the persistent transitionend listener (removed in ngOnDestroy). */
  private sidebarTransitionListenerCleanup: (() => void) | null = null;
  /** Cleanup for the persistent mousemove listener (removed in ngOnDestroy). */
  private pointerMoveListenerCleanup: (() => void) | null = null;

  private subscription = new Subscription();
  private loadingService?: LoadingService;

  /**
   * TASK-60341/US #60630: key → overlay-open action. Replaces an if/else chain
   * that would grow one branch per new overlay submodule (code-quality review
   * finding, code duplicated with HomePageComponent.onQuickAccessClick — each
   * component keeps its own map, because the modal services it opens are this
   * component's own dependencies, not shared state).
   *
   * Keyed by `OverlaySubmoduleKey`, so the map is EXHAUSTIVE over
   * `OVERLAY_SUBMODULE_KEY_LIST` by type: a key added to that list and forgotten
   * here does not compile. Do not widen the key back to `string` — that is what
   * turns the omission into a dead menu leaf on this surface alone.
   */
  private readonly overlayOpeners: Readonly<Record<OverlaySubmoduleKey, () => void>> = {
    [SUBMODULE_KEY_ESTABELECIMENTO_SAUDE]: () => this.healthFacilitiesModal.open(),
    [SUBMODULE_KEY_CADASTRO_FILAS_COLETA]: () => this.collectionQueuesModal.open(),
    [SUBMODULE_KEY_CADASTRO_CLINICAS]: () => this.clinicsModal.open(),
    [SUBMODULE_KEY_VACINAS_CONFIGURACAO]: () => this.vaccinesConfigModal.open(),
    [SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO]: () => this.laboratorySectorsModal.open(),
    [SUBMODULE_KEY_CADASTRO_SOLICITANTES]: () => this.solicitantesModal.open(),
    // US #62189: Cadastros › Tabela de Preços.
    [SUBMODULE_KEY_CADASTRO_TABELA_PRECO]: () => this.priceTablesModal.open(),
    [SUBMODULE_KEY_CADASTRO_PACIENTE]: () => this.patientRegistryModal.open(),
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private sidebarState: SidebarStateService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private notificationService: NotificationService,
    private i18n: I18nService,
    private agentPrintService: AgentPrintService,
    private injector: Injector,
    private userAccess: UserAccessService,
    private healthFacilitiesModal: HealthFacilitiesModalService,
    private collectionQueuesModal: CollectionQueuesModalService,
    private vaccinesConfigModal: VaccinesConfigModalService,
    private laboratorySectorsModal: LaboratorySectorsModalService,
    private solicitantesModal: SolicitantesModalService,
    private priceTablesModal: PriceTablesModalService,
    private clinicsModal: ClinicsModalService,
    private patientRegistryModal: PatientRegistryModalService,
    private menuFocus: MenuFocusService,
    private host: ElementRef<HTMLElement>,
    private modalSurfaces: ModalSurfaceRegistry
  ) {}

  ngOnInit(): void {
    this.loadMenuFromBackend();
    this.currentUrl = this.router.url.split('?')[0];
    // Recompute the active path whenever the user navigates.
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.currentUrl = this.router.url.split('?')[0];
          this.activePath = this.computeActivePath(this.menuItems, this.currentUrl);
          this.closeFlyout();
        })
    );

    this.loadingService = this.injector.get(LoadingService);
  }

  ngAfterViewInit(): void {
    // Persistent transitionend listener that clears `sidebarTransitioning`
    // every time the `.sidebar` finishes its `width` animation. This is the
    // counterpart to the setter that latches the flag — together they form
    // the single source of truth that `deferUntilSidebarExpanded` reads
    // to decide whether to run immediately or wait (TASK-59001 follow-up).
    const el = this.sidebarEl?.nativeElement;
    if (el) {
      const onTransitionEnd = (event: TransitionEvent): void => {
        // Strict target check so a bubbling transitionend from a child
        // element never accidentally clears the flag mid-animation.
        if (event.target === el && event.propertyName === 'width') {
          this.sidebarTransitioning = false;
        }
      };
      el.addEventListener('transitionend', onTransitionEnd);
      this.sidebarTransitionListenerCleanup = (): void => {
        el.removeEventListener('transitionend', onTransitionEnd);
        this.sidebarTransitionListenerCleanup = null;
      };
      // While armed, the guard follows the cursor's rest point; the move's own boundary events came first.
      const onPointerMove = (event: MouseEvent): void => {
        this.lastPointer = pointOf(event);
        if (this.layoutShiftGuard !== null) this.layoutShiftGuard = this.lastPointer;
      };
      // Outside the zone: mousemove fires too often to run change detection per event.
      this.zone.runOutsideAngular(() => el.addEventListener('mousemove', onPointerMove));
      this.pointerMoveListenerCleanup = (): void => {
        el.removeEventListener('mousemove', onPointerMove);
        this.pointerMoveListenerCleanup = null;
      };
    }
    // Defensive reset: if the very first @Input flush flipped `_isOpen` it
    // would have latched the flag, but the initial CSS transition could have
    // ended before this listener was wired up — leaving the flag stuck `true`
    // until the next manual toggle. Resetting here gives every user
    // interaction a clean baseline; the safety timeout in
    // `deferUntilSidebarExpanded` still covers any genuinely-running
    // transition that happens to be in flight.
    this.sidebarTransitioning = false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.cancelHoverTimer();
    this.sidebarTransitionListenerCleanup?.();
    this.pointerMoveListenerCleanup?.();
    if (this.pointerPressTimer !== null) clearTimeout(this.pointerPressTimer);
    // Leaf activation schedules a deferred handoff — to the destination page's
    // content region, or back onto the menu after an external launch — and both
    // outlive this teardown otherwise, firing into a menu that is gone. Only
    // what the sidebar itself owns is released, never a panel's (cause C-01).
    this.menuFocus.cancel(this);
  }

  /** Converts a backend icon name into a Lucide icon name (template usage). */
  getLucideIcon(iconSvg: string | undefined): string {
    return backendIconToLucide(iconSvg) || 'file-text';
  }

  /**
   * Tracking key of a root item, so a re-render that rebuilds every `MenuItem`
   * object reuses the `<a>` elements instead of destroying and recreating them
   * (RN-18). Without it the browser drops the keyboard on `<body>` on every
   * permission or language re-emission, and there is no element left to hand it
   * back to. Same key the panel tracks by, and the same one the DOM carries.
   */
  trackByItem = (_index: number, item: MenuItem): string => menuItemKey(item);

  /**
   * `menuItemKey` as something the template can call, so `data-item-id` is
   * WRITTEN by the same rule the resolution READS. Spelled out inline
   * (`item.id ?? item.label`), the attribute and the function are one edit away
   * from disagreeing — and the day they do, every key path stops matching and
   * the recovery fires on every single re-render, silently.
   */
  itemKey = menuItemKey;

  /**
   * `0` on the one root item the keyboard comes back to, `-1` on every other —
   * the roving tabindex that makes the whole menu a single Tab stop (AC7, D5).
   *
   * Answered per item on every pass rather than cached against a field: the
   * roving target moves on any handoff the menu makes and on any re-render that
   * prunes it, so a cached copy would need invalidating at both, and a stale one
   * fails the way this WI is about — a menubar with the stop on an item that is
   * no longer there. A menu is a dozen items; the scan is not worth a second
   * source of truth.
   */
  rootTabIndex(item: MenuItem): number {
    return menuItemKey(item) === this.rovingRootKey() ? 0 : -1;
  }

  /**
   * Root item that carries the menu's only tab stop right now.
   *
   * The stop lives on a root item whatever the keyboard last touched, so the
   * remembered path is read down to its OUTERMOST segment: a panel item hands
   * the stop to the root that opens it, which is the deepest part of that path
   * still on screen once the cascade is gone — step 2 of AC6's chain, on a
   * menubar where step 1 and step 2 name the same element.
   *
   * The outermost segment is read without a guard for its absence, the same way
   * the service reads `data-item-id`: a recorded path always carries at least
   * the key of the item it was recorded on, and `menuKeyPathSegments` drops
   * empty segments — so an "empty path" branch is one no arrangement can reach.
   */
  private rovingRootKey(): string {
    const remembered = this.menuFocus.rememberedKeyPath();
    const rootKey = remembered === null ? null : menuKeyPathSegments(remembered)[0];
    // Step 3. Asked of the tree that is rendered NOW, because a branch the
    // user visited can be gone by the time they tab back — a permission check
    // resolving, a language switch. Without it the menubar ends the pass with
    // NO tab stop at all, and a menu no Tab can enter is worse than one whose
    // stop sits in the wrong place.
    const survives = rootKey !== null && this.menuItems.some(item => menuItemKey(item) === rootKey);
    return survives ? rootKey : this.firstRootKey();
  }

  /**
   * Key of the first root item — the last address the roving has (AC6, step 3).
   *
   * Written without a guard for an empty menu because there is no arrangement
   * that asks for one: the only caller is `rootTabIndex`, and the template calls
   * that once per RENDERED root item, so a menu with no items never gets here.
   * A guard would only add a branch no test can reach.
   */
  private firstRootKey(): string {
    return menuItemKey(this.menuItems[0]);
  }

  hasChildren(item: MenuItem): boolean {
    return !!item.children && item.children.length > 0;
  }

  /**
   * Compared by stable key, never by reference: `applyMenu` rebuilds every
   * `MenuItem` from the DTOs, so `openItem === item` goes false the instant a
   * permission check or a language switch re-emits the tree — and the panel
   * would stay on screen while the item anchoring it stopped announcing itself
   * as expanded (cause C-04).
   */
  isOpenRoot(item: MenuItem): boolean {
    return this.openItem !== null && menuItemKey(this.openItem) === menuItemKey(item);
  }

  isOnActivePath(item: MenuItem): boolean {
    const key = item.id ?? item.label;
    return this.activePath.has(key);
  }

  /**
   * Click on a sidebar root item: opens the flyout (if it has children), navigates
   * or opens an external URL. The captured anchor is used by the flyout to position itself.
   *
   * When the sidebar is collapsed and the item has children, also emits the
   * request to expand the sidebar — the flyout opens in the same gesture, no second click needed.
   */
  async onRootClick(item: MenuItem, event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.cancelHoverTimer();
    this.lastPointer = pointOf(event);

    if (this.hasChildren(item)) {
      const trigger = event.currentTarget as HTMLElement;
      if (!this.isOpen) {
        this.layoutShiftGuard = this.lastPointer;
        // Latch the transition flag synchronously before emitting. The
        // setter on `@Input isOpen` also latches it, but only when Angular
        // flushes the binding — *after* this handler returns. Without this
        // early latch, the helper below would observe `sidebarTransitioning
        // === false` and run the action immediately, defeating the purpose.
        this.sidebarTransitioning = true;
        this.requestSidebarOpen.emit();
      }
      const key = menuItemKey(item);
      // ALWAYS go through the helper — covers both "sidebar just got
      // requested to open" (latched above) and "sidebar is mid-transition
      // because another path toggled it" (latched by the setter).
      this.deferUntilSidebarExpanded(
        () => this.zone.run(() => {
          // Re-asked of the tree that is on screen NOW, for the reason
          // `openCascadeFromKeyboard` spells out at its own landing: the wait
          // outlasts a permission check, and the `MenuItem` the gesture carried
          // is an object of the emission before last. Landed as captured, the
          // panel renders children the check has just denied (cause C-04).
          const current = findOpenableChild(this.menuItems, key);
          if (!current) return;
          this.declareTheCascadeTheMouses();
          this.toggleFlyout(current, trigger);
        }),
        { key, fromKeyboard: false }
      );
      return;
    }

    // Leaf — follows the product's special navigation (external AVD links, etc.).
    await this.activateLeaf(item);
  }

  /**
   * Hover on a root item: schedules an action after `HOVER_DELAY_MS`.
   *
   * - Sidebar collapsed: expands the sidebar (any item, leaf or parent).
   * - Sidebar collapsed + item with children: also opens the flyout.
   * - Sidebar expanded + item with children: opens the flyout.
   * - Sidebar expanded + leaf item: nothing (no flyout to show).
   * - Entry at the guarded rest point (layout shift, CG-FRONT-026): ignored.
   *
   * Uses `cdr.detectChanges()` in the callback because, in some scenarios (e.g.
   * a timer firing outside the natural zone tick), `markForCheck` was not
   * enough to make the flyout's `*ngIf` re-evaluate — the panel only appeared
   * on the next event.
   */
  onRootMouseEnter(item: MenuItem, event: MouseEvent): void {
    if (this.isLayoutShift(event)) return;
    this.lastPointer = pointOf(event);
    if (this.hoverSuppressed) return; // user just selected a leaf
    const isParent = this.hasChildren(item);
    const sidebarClosed = !this.isOpen;
    // Hover on a leaf with the sidebar already open: nothing to do.
    if (!sidebarClosed && !isParent) return;
    this.cancelHoverTimer();
    const trigger = event.currentTarget as HTMLElement;
    this.hoverTimer = setTimeout(() => {
      this.zone.run(() => {
        this.hoverTimer = null;
        if (sidebarClosed) {
          this.layoutShiftGuard = this.lastPointer;
          // Same early-latch as onRootClick — see the comment there for
          // the race window explanation.
          this.sidebarTransitioning = true;
          this.requestSidebarOpen.emit();
        }
        if (!isParent) return;
        const key = menuItemKey(item);
        const openFlyout = (): void => {
          // Re-asked of the tree on screen NOW, the move `openCascadeFromKeyboard`
          // already makes at its own landing: the hover's wait outlasts a
          // permission check, and the `MenuItem` the gesture carried belongs to
          // an emission that is gone. Landed as captured, the panel renders
          // children the check has just DENIED (cause C-04). That is the half a
          // test reads — "an opening the MOUSE parked shows the tree the user is
          // looking at".
          //
          // The key comparison below is defence in depth and is NOT observable:
          // mutated back to `this.openItem !== item` the suite stays green,
          // because with `current` re-resolved here `openItem` and it are the
          // same object of the same tree — `rebindOpenCascade` re-points
          // `openItem` off that very tree. It is written by key anyway so that
          // "is this the item that is already open" has ONE spelling in this
          // component, the day something reintroduces an object from elsewhere.
          const current = findOpenableChild(this.menuItems, key);
          if (!current) return;
          if (this.openItem === null || menuItemKey(this.openItem) !== key) {
            this.declareTheCascadeTheMouses();
            this.openItem = current;
            this.openAnchor = trigger;
            this.cdr.detectChanges(); // mounts the <app-nav-flyout>
            // Follow-up CD: the freshly-mounted flyout's ngAfterViewInit calls
            // calculatePosition + markForCheck on itself, but the dirty flag
            // is only flushed on the next bound event. A microtask CD pass
            // makes the panel appear immediately.
            Promise.resolve().then(() => this.cdr.detectChanges());
          }
        };
        // Always go through the helper — covers the case "sidebar is
        // currently mid-transition because something else (hamburger,
        // hover) just toggled it" too. Helper short-circuits to immediate
        // when no transition is in progress.
        this.deferUntilSidebarExpanded(
          () => this.zone.run(openFlyout),
          { key: menuItemKey(item), fromKeyboard: false }
        );
      });
    }, SidebarComponent.HOVER_DELAY_MS);
  }

  /**
   * States that the cascade about to go on screen owes the keyboard nothing —
   * what every MOUSE opening of a root item has to say.
   *
   * `keyboardOpenedItem` is only cleared when the cascade closes, so with a
   * panel open the whole time the cursor merely had to wander to another root
   * item and come back for `flyoutAutofocus` to pair up again: the panel was
   * re-opened on a live instance, armed its opening latch and took the keyboard
   * out of wherever the user had walked to (AC8). Said by the gesture rather
   * than inferred later, because only the gesture knows.
   */
  private declareTheCascadeTheMouses(): void {
    this.keyboardOpenedItem = null;
  }

  /** Whether a boundary event fired at the guarded point, i.e. the layout moved, not the cursor. */
  private isLayoutShift(event: MouseEvent): boolean {
    return this.layoutShiftGuard !== null && samePoint(event, this.layoutShiftGuard);
  }

  /** Mouse leaves the root item: cancels the scheduled open, unless the row moved under a parked cursor (CG-FRONT-026). */
  onRootMouseLeave(event?: MouseEvent): void {
    // A direct call carries no event and counts as a real exit.
    if (event && this.isLayoutShift(event)) return;
    this.cancelHoverTimer();
  }

  /**
   * Mouse leaves the whole sidebar: clears any hover-suppression left over
   * from a leaf selection. The next `mouseenter` on a parent item works
   * normally again (an explicit gesture from the user).
   */
  onSidebarMouseLeave(): void {
    this.layoutShiftGuard = null;
    if (this.hoverSuppressed) {
      this.hoverSuppressed = false;
      if (this.hoverSuppressTimer !== null) {
        clearTimeout(this.hoverSuppressTimer);
        this.hoverSuppressTimer = null;
      }
    }
    this.cancelHoverTimer();
  }

  /**
   * Leaf selected (at any flyout level): navigate, close the cascade and
   * collapse the sidebar.
   *
   * `fromKeyboard` comes from the panel, which is the only place that knows
   * which gesture picked the item, and it defaults to the mouse so a caller
   * that says nothing stays outside the focus contract (D7).
   */
  async onLeafActivated(item: MenuItem, fromKeyboard = false): Promise<void> {
    await this.activateLeaf(item, fromKeyboard);
    this.closeFlyout();
    this.requestSidebarClose.emit();
    // Suppress hovers until the user leaves the sidebar (moves the cursor away)
    // or the safety timer fires — prevents reopening on the same gesture.
    this.startHoverSuppression();
  }

  private startHoverSuppression(): void {
    this.cancelHoverTimer();
    this.layoutShiftGuard = null;
    this.hoverSuppressed = true;
    if (this.hoverSuppressTimer !== null) clearTimeout(this.hoverSuppressTimer);
    // Safety net: if the user keeps the cursor parked over the sidebar and
    // never leaves, suppression expires by itself after this period.
    this.hoverSuppressTimer = setTimeout(() => {
      this.zone.run(() => {
        this.hoverSuppressed = false;
        this.hoverSuppressTimer = null;
      });
    }, SidebarComponent.HOVER_SUPPRESS_MS);
  }

  /**
   * Keyboard navigation on root items (vertical WAI-ARIA Menubar pattern):
   * - ArrowUp/Down: focus previous/next sibling (with wrap-around).
   * - Home/End: focus the first/last item.
   * - Enter/Space: activate (open the flyout or navigate), like a click.
   * - ArrowRight: if the item has children, opens the flyout and moves focus to the first item.
   * - ArrowLeft: closes ANY open cascade, not only this item's — the panel on
   *   screen may belong to another root item, and the key still dismisses it,
   *   leaving the keyboard where it is (AC17, D14).
   */
  async onRootKeydown(item: MenuItem, event: KeyboardEvent, index: number): Promise<void> {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusRootByIndex((index + 1) % this.menuItems.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusRootByIndex((index - 1 + this.menuItems.length) % this.menuItems.length);
        break;
      case 'Home':
        event.preventDefault();
        this.focusRootByIndex(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusRootByIndex(this.menuItems.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.cancelHoverTimer();
        if (this.hasChildren(item)) {
          this.openCascadeFromKeyboard(item, (target, anchor) => this.toggleFlyout(target, anchor));
        } else {
          await this.activateLeaf(item, true);
        }
        break;
      case 'ArrowRight': {
        if (!this.hasChildren(item)) return;
        event.preventDefault();
        this.cancelHoverTimer();
        this.openCascadeFromKeyboard(item, (target, anchor) => {
          // Asked by stable key, like `toggleFlyout` and `onRootMouseEnter`'s
          // `openFlyout`: "is this the item that is already open" has ONE
          // spelling in this component. Defence in depth and NOT observable
          // here either — `target` is re-resolved against `this.menuItems` at
          // landing time and `rebindOpenCascade` re-points `openItem` off that
          // same tree, so a mutation back to `this.openItem !== target` leaves
          // the suite green. Written this way so the day something reintroduces
          // an item captured at gesture time, `ArrowRight` on an already-open
          // cascade still re-states it instead of re-opening it against a
          // reference from an emission that is gone.
          const alreadyOnScreen = this.isOpenRoot(target);
          if (!alreadyOnScreen) {
            this.openItem = target;
            this.openAnchor = anchor;
            this.cdr.markForCheck();
          }
          this.handKeyboardToLevelOne(alreadyOnScreen);
        });
        break;
      }
      case 'ArrowLeft':
        // Any open cascade, not only this item's: the panel on screen may
        // belong to another root item — opened by hover, or left behind by the
        // mouse — and ArrowLeft is still the key that dismisses it (AC17, D14).
        // Guarded on `openItem` because with nothing open there is nothing to
        // close, and the key belongs to whoever else wants it — but an opening
        // still waiting for the sidebar to finish expanding has not written
        // `openItem` yet and is just as much the menu's to dismiss. Read off
        // that field alone, this key went to whoever else wanted it while the
        // cascade the user had just cancelled appeared anyway (AC15).
        //
        // Read broader than the Escape gate on purpose: this key already
        // dismisses a cascade the MOUSE put on screen (AC17), so the one the
        // mouse has parked is just as much its business — and the keyboard is
        // demonstrably on a root item, which `Esc` has to establish first.
        if (this.openItem === null && this.pendingExpandCleanup === null) return;
        event.preventDefault();
        // No focus move: the keyboard is already on a root item, which is
        // exactly where a return would put it (AC1 edge, AC17).
        this.cancelPendingExpandAction();
        this.closeFlyout();
        break;
    }
  }

  /**
   * Opens `item`'s cascade for a key press, once the sidebar has stopped
   * expanding.
   *
   * The keyboard was the one path that skipped the wait the click and the hover
   * both make: `requestSidebarOpen` and the assignment of `openItem` sat on
   * consecutive lines, so the panel measured an anchor still mid-`width` and
   * opened beside where the item had been — while the sidebar kept sliding out
   * from under it (AC15).
   *
   * `open` is handed in rather than branched on here because the two keys ask
   * for different things on an already-open cascade: `Enter` toggles it shut,
   * `ArrowRight` re-states the opening it is already looking at.
   */
  private openCascadeFromKeyboard(
    item: MenuItem,
    open: (item: MenuItem, anchor: HTMLElement) => void
  ): void {
    const key = menuItemKey(item);
    if (!this.isOpen) {
      // Latch the transition flag synchronously before emitting — the same
      // window the click path documents: the `@Input isOpen` setter latches it
      // too, but only when Angular flushes the binding, which is after this
      // handler returns. Until then the helper below would read "nothing is
      // moving" and open against the collapsed width.
      this.sidebarTransitioning = true;
      this.requestSidebarOpen.emit();
    }
    // ALWAYS through the helper: it short-circuits to synchronous when no
    // transition is in flight, and it also covers the sidebar being
    // mid-transition because another path (hamburger, hover) toggled it.
    this.deferUntilSidebarExpanded(() => this.zone.run(() => {
      // The keys that move the keyboard on cancel this wait on their way
      // through; walking OUT of the menu — `Tab` to the footer, a click into a
      // field — presses nothing the menu hears, and the wait outlasts it. The
      // same membership test `Escape` reads, against the same two scopes:
      // `not-ours` is exactly "the keyboard is on a real element outside the
      // menu", so the opening lands on a user who left and pulls them back into
      // a panel they walked away from (AC4, AC15).
      //
      // Read at landing time rather than through a `focusout` listener: the
      // window between a blur and the next focus reads as `<body>` — normal DOM,
      // not a departure — and a listener would cancel legitimate handoffs inside
      // it, which is the observation point this plan declares normative.
      if (this.escapeAudience() === 'not-ours') return;
      // Re-asked of the tree that is on screen NOW, by the same predicate
      // `rebindOpenCascade` re-points an open cascade by and
      // `keyPathStillRendered` walks with. Those two read `openItem`, and a wait
      // that has not landed has not written it — so a re-render during the
      // expansion prunes the branch with nothing to see the pending opening,
      // and it arrives anyway, against a tree that is gone (AC6, AC9).
      const current = findOpenableChild(this.menuItems, key);
      const anchor = this.rootLinkFor(key);
      // `!current` is the live half — that is the pruned branch above. `!anchor`
      // is defensive and unreachable today: the two answer off different
      // sources (the data tree vs. the rendered `<a>`s), and they can only
      // disagree in the window between `menuItems` being assigned and the
      // change detection that mounts it — a window production never lands in,
      // because every emission path marks for check and the pass always runs
      // before this listener does. Kept because the day the two do drift, the
      // alternative is a panel positioned against `undefined`. Deliberately
      // uncovered: no test can reach it without faking the view children.
      if (!current || !anchor) return;
      // Recorded with the opening it describes, never at key time:
      // `flyoutAutofocus` pairs this item with `openItem`, so announcing a
      // keyboard opening while the sidebar is still moving would describe a
      // cascade that is not on screen — and that a newer key may yet cancel.
      this.keyboardOpenedItem = current;
      open(current, anchor);
    }), { key, fromKeyboard: true });
  }

  /**
   * Hands the keyboard to the first item of the level-1 panel — what
   * `ArrowRight` on a root item promises, and it promises it on EVERY press,
   * whether this one had to put the panel on screen or found it already there
   * (AC5, "the first item takes the focus on every opening").
   *
   * The sidebar used to make no such promise: it wrote `openItem` and left the
   * move to the panel's own opening latch, which arms on `items` changing or on
   * `autofocus` rising `false -> true`. On a panel the keyboard had ALREADY
   * opened and then walked away from — `Tab` out of the menu leaves the cascade
   * up (NEW-04, QA-11) — neither happens: `ArrowRight` re-stated an opening
   * that was already on screen and already the keyboard's, no input changed,
   * no latch armed, and the key was a no-op the user could only escape with
   * `ArrowLeft` followed by `ArrowRight`.
   *
   * **`alreadyOnScreen` is the press's own answer, never a fresh reading of the
   * DOM.** Asked as "is a panel rendered" it comes back true while the panel on
   * screen still belongs to the PREVIOUS root item — the press that switches
   * branches has not been rendered yet — and the keyboard would be handed to an
   * item this press is in the middle of replacing.
   */
  private handKeyboardToLevelOne(alreadyOnScreen: boolean): void {
    // Already mounted, so it takes the keyboard inside the keystroke that asked
    // for it. Deferred, the move would arrive after the next key of an
    // auto-repeat — which is born on the item the focus has not left yet — and
    // the two presses would spend themselves on one move (AC14, and the
    // `focusNow` half of the plan's normative rule).
    if (alreadyOnScreen) {
      this.menuFocus.focusNow(this.firstLevelOneItem());
      return;
    }
    // The panel this press opens is mounted by the change detection the press
    // triggers, so the target crosses a render and the handoff is deferred —
    // the `scheduleFocus` half of the same rule. **Superseded in practice and
    // NOT discriminated by any test: a mutation that drops this line leaves the
    // suite green.** Said plainly rather than left for the next reader to
    // discover: every path that mounts or re-points the level-1 panel also
    // arms the panel's own opening latch, and the panel then schedules over
    // this handoff — same owner-cancelling slot, same first item. It stays
    // because the promise above is the SIDEBAR's, and delegating it to a latch
    // with arming conditions of its own is exactly the coupling that produced
    // the dead end this method closes.
    this.menuFocus.scheduleFocus({ resolve: () => this.firstLevelOneItem() }, this);
  }

  /**
   * First item of the level-1 panel — the OUTERMOST level of the cascade, never
   * one of the levels below it.
   *
   * Contained on the panel this component renders itself (`:scope >`), against
   * the very handles the focus owner resolves with: a panel is
   * `MENU_PANEL_SCOPE` and an item is what carries `data-item-id`. A
   * document-wide lookup would be answered by whichever panel of the cascade
   * comes first in document order — a coincidence of the current template, not
   * a rule — and with three levels open and the keyboard back on the root item,
   * `ArrowRight` would land it somewhere down the cascade the user never asked
   * for, silently.
   */
  private firstLevelOneItem(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>(
      `:scope > app-nav-flyout > ${MENU_PANEL_SCOPE} [data-item-id]`
    );
  }

  /**
   * When the root flyout asks to close (e.g. ArrowLeft on the first level or
   * Esc): closes the cascade and returns focus to the original root item.
   */
  onFlyoutClose(): void {
    const itemToRefocus = this.openItem;
    this.closeFlyout();
    if (itemToRefocus) {
      // Located by stable key, never by `indexOf`: a re-render swaps the objects
      // in `menuItems` and the reference lookup then returns -1 — silently, so
      // the return was skipped and the keyboard was left on `<body>` (C-04).
      const key = menuItemKey(itemToRefocus);
      const idx = this.menuItems.findIndex(item => menuItemKey(item) === key);
      // The root <a> is already mounted and is not part of what the closing
      // panel takes down — moving the focus inside the keystroke is what makes
      // it survive the teardown pass, instead of bouncing through <body>.
      if (idx >= 0) this.focusRootByIndex(idx);
    }
  }

  /**
   * Moves the keyboard focus to the root item at `index` **now**, dropping any
   * pending handoff. Sibling navigation stays inside the keystroke that asked
   * for it: the item already exists, and deferring it loses keys to auto-repeat
   * (TASK-61043, AC14).
   *
   * An opening still waiting for the sidebar to finish expanding goes with it.
   * That wait lasts a whole `width` transition — longer than key auto-repeat —
   * and the cascade it would put on screen belongs to an item the keyboard has
   * just left, so landing it means an older key overruling the newest one and
   * pulling the focus into a panel nobody asked for (AC14, AC15).
   */
  private focusRootByIndex(index: number): void {
    this.cancelPendingExpandAction();
    this.menuFocus.focusNow(this.rootLinkAt(index));
  }

  /**
   * Root `<a>` carrying `key` in the tree as it is rendered at this instant, or
   * `null` when that item is not on screen.
   */
  private rootLinkFor(key: string): HTMLElement | null {
    return this.rootLinkAt(this.menuItems.findIndex(item => menuItemKey(item) === key));
  }

  /** Root `<a>` at `index` in the tree as it is rendered at this instant. */
  private rootLinkAt(index: number): HTMLElement | null {
    const links = this.rootLinks?.toArray() ?? [];
    if (index < 0 || index >= links.length) return null;
    return links[index].nativeElement;
  }

  private cancelHoverTimer(): void {
    if (this.hoverTimer !== null) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    // Also drop any deferred-until-expanded action — the user's new gesture
    // (mouseleave, leaf selection, click elsewhere) supersedes the pending one.
    this.cancelPendingExpandAction();
  }

  /**
   * Defers `action` until the sidebar's `width` CSS transition completes —
   * replaces the previous fixed `setTimeout(SIDEBAR_TRANSITION_MS)` approach
   * (TASK-59001). Time-based waits could fire 1–2 frames before the actual
   * `transitionend` on slow machines or under change-detection pressure,
   * leaving the flyout opening against a still-moving anchor.
   *
   * If the sidebar is already at its expanded width (no pending transition)
   * the action runs synchronously. Otherwise we attach a one-shot
   * `transitionend` listener filtered to `propertyName === 'width'`, with a
   * safety timeout so the deferred action runs even if the browser never
   * fires `transitionend` (e.g. element detached, transition interrupted).
   */
  private deferUntilSidebarExpanded(action: () => void, parked: ParkedOpening | null = null): void {
    const el = this.sidebarEl?.nativeElement;
    // No element, or no transition currently in progress → run synchronously.
    // The flag is the authoritative source: `clientWidth` reflects the target
    // computed style (not the animated value) as soon as the class flips, so
    // it would mis-report "already expanded" mid-transition. The flag, set in
    // the `isOpen` setter and cleared by transitionend, doesn't have that bug.
    if (!el || !this.sidebarTransitioning) {
      action();
      return;
    }

    // Latest call wins — cancel any earlier pending wait.
    this.cancelPendingExpandAction();

    let invoked = false;
    // Every way out of the wait — it landed, it was superseded, it was
    // cancelled — releases the same two fields together, so "who parked this"
    // can never outlive the parking it describes.
    const release = (): void => {
      el.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(safetyTimer);
      this.pendingExpandCleanup = null;
      this.pendingOpen = null;
    };
    const runOnce = (): void => {
      if (invoked) return;
      invoked = true;
      release();
      action();
    };

    const onTransitionEnd = (event: TransitionEvent): void => {
      if (event.propertyName !== 'width') return;
      runOnce();
    };

    el.addEventListener('transitionend', onTransitionEnd);
    // CSS transition is 0.28s — give it ~2× as a hard cap covering slow
    // machines, throttled timers in background tabs, and interrupted transitions.
    const safetyTimer = setTimeout(runOnce, SidebarComponent.SIDEBAR_TRANSITION_SAFETY_MS);

    this.pendingOpen = parked;
    this.pendingExpandCleanup = (): void => {
      // Flag invoked so any racing transitionend / safety fire is a no-op
      // even after we strip our own listener.
      invoked = true;
      release();
    };
  }

  private cancelPendingExpandAction(): void {
    this.pendingExpandCleanup?.();
  }

  private toggleFlyout(item: MenuItem, anchor: HTMLElement): void {
    // "The same item is already open" is a question about identity, and identity
    // in this menu is the key. Defence in depth, and NOT observable: every caller
    // now hands in an item resolved off `this.menuItems` at landing time, and
    // `rebindOpenCascade` re-points `openItem` off that same tree, so a mutation
    // back to `this.openItem === item` leaves the suite green. Written by key so
    // that the day a caller passes an item captured at gesture time again — the
    // defect the landings above close — a second click on an open cascade is
    // still read as a close instead of re-opening what it was asked to dismiss.
    if (this.openItem !== null && menuItemKey(this.openItem) === menuItemKey(item)) {
      this.closeFlyout();
      return;
    }
    this.openItem = item;
    this.openAnchor = anchor;
    this.cdr.markForCheck();
  }

  private closeFlyout(): void {
    if (this.openItem === null) return;
    this.openItem = null;
    this.openAnchor = null;
    this.keyboardOpenedItem = null;
    this.cdr.markForCheck();
  }

  /**
   * Função única e genérica de ativação de um item-folha (AC3/AC6).
   * Resolve a URL de lançamento externo na ordem:
   *   1) URL dinâmica do backend (externalLaunchUrlTemplate / env) — precisa por módulo/tenant;
   *   2) mapa ESTÁTICO por rota (AC1) — fallback para módulo executável conhecido;
   *   3) externalUrl explícito.
   * Se resolver, abre em nova aba com noopener,noreferrer (AC5) e o e-mail já propagado (AC4).
   * Caso a rota NÃO seja um módulo executável, faz navegação interna via Angular Router (AC10).
   * Nenhum token interno (JWT/cookies) é propagado — só o e-mail no template (AC9).
   */
  private async activateLeaf(item: MenuItem, fromKeyboard = false): Promise<void> {
    // TASK-60341/US #60630: Cadastros → Estabelecimento de Saúde and
    // Filas de Coleta are OVERLAYS, not routes. Open
    // the matching modal on top of the current page via its modal service and
    // stop here — no external-launch resolution, no Angular navigation.
    // Permission gating already hides this leaf when the user lacks access,
    // so reaching this branch means the item is allowed.
    if (opensAsOverlay(item.key)) {
      // Indexed directly, not optionally: `opensAsOverlay` narrows the key to
      // `OverlaySubmoduleKey`, over which the map is exhaustive by type. An `?.()`
      // here would compile away a missing opener into a silent no-op.
      this.overlayOpeners[item.key]();
      // Destination 1 of 3 (AC19, AC4): the overlay takes the keyboard itself —
      // the app's modals focus their first field as they open. The menu's part
      // is to hand over and schedule NOTHING: a return of its own would land
      // after that and pull the keyboard back out of the overlay, onto an item
      // the closing cascade has just removed. Hence the early return, which is
      // what keeps the two moves below out of this branch.
      return;
    }

    const email = this.authService.currentUserValue?.email;
    const apiOnly = environment.externalLaunchUrlsFromApiOnly === true;

    // 1) URL dinâmica do backend (prioridade — URL precisa por módulo/tenant).
    let url = resolveExternalLaunchUrlChain({
      template: item.externalLaunchUrlTemplate,
      submoduleKey: item.key,
      email,
      externalMenuLinks: environment.externalMenuLinks,
      apiOnly,
    });

    // 2) Mapa estático por rota (AC1) — fallback só quando NÃO é apiOnly.
    //    Em dev/prod (apiOnly=true) a URL vem SOMENTE da resolução dinâmica do backend (sem hardcode).
    if (!url && !apiOnly) {
      url = resolveExecutableModuleLaunchUrl(item.route, email);
    }

    // 3) URL externa explícita.
    if (!url && item.externalUrl) {
      url = item.externalUrl;
    }

    if (url) {
      // Destination 2 of 3 (AC19, AC4): the launch opens ANOTHER document and
      // this one does not change, so there is no destination content region of
      // ours to hand the keyboard to — and none the browser would give us
      // inside the tab it is about to open. It stays on the menu, on the item
      // it is standing on, which for a panel leaf means the root item that
      // anchors the cascade once that cascade comes down.
      if (fromKeyboard) this.keepTheKeyboardOnTheMenuItStandsOn();
      // Reuso de aba: links do AVD abrem SEMPRE na mesma aba nomeada ('autolac-avd') —
      // ao clicar outro módulo, reaproveita a aba existente em vez de abrir uma nova
      // (só cria nova se não houver). Para isso o alvo precisa ser nomeado e SEM 'noopener'
      // (noopener isola a aba e impede o reuso); como o destino é o cliente AVD da Microsoft,
      // o risco de reverse-tabnabbing é desprezível. Demais URLs externas seguem com
      // _blank + noopener,noreferrer (AC5).
      const isAvd = url.indexOf('windows.cloud.microsoft') !== -1 || url.indexOf('/webclient/avd/') !== -1;
      let avdWindow: Window | null = null;

      if (isAvd) {
        const paired = await this.pairLocalAgentForAvd(true);

        if (paired) {
          window.open(url, 'autolac-avd');
          return;
        }

        avdWindow = window.open('/avd-loading', 'autolac-avd');

        if (!avdWindow) {
          this.notificationService.error(this.i18n.translate('layout.sidebar.toast.errorBrowser'));
          return;
        }

        this.loadingService?.show();
        this.notificationService.warning(this.i18n.translate('layout.sidebar.toast.lookingPrintAgent'));
        await this.pairLocalAgentForAvd(false);
        this.loadingService?.hide();

        avdWindow.location.href = url;
      } else {
        avdWindow = window.open(url, '_blank', 'noopener,noreferrer');
      }

      return;
    }

    // 4) Não é módulo executável → navegação interna padrão (AC10).
    if (item.route) {
      const navigated = this.router.navigateByUrl(item.route);
      // Destination 3 of 3 (AC19, AC4, D15): the page itself changes, so the
      // keyboard belongs in the content region of the page it changed to — and
      // only then, which is why the router is asked whether it did.
      if (fromKeyboard) this.landTheKeyboardOnceTheRouterAnswers(navigated);
    }
    // An item with none of the three — no overlay key, no launch URL and no
    // route — has no destination at all, and so no landing place to declare.
    // Every move above hangs off the branch that decided a destination, which
    // is what keeps such an item as inert as it is today (NEW-06, AC11).
  }

  /**
   * Keeps the keyboard on the menu item it is standing on, resolved again once
   * the cascade around it has come down (AC19, AC4).
   *
   * Deferred, and by key path rather than by element: the item the keyboard is
   * on right now is about to be removed by the caller, and the answer only
   * exists after that. `MenuFocusService` then resolves the longest surviving
   * prefix of the path — which for a panel leaf is the root item that anchored
   * the cascade, and for a root leaf is that same root item, still on screen.
   */
  private keepTheKeyboardOnTheMenuItStandsOn(): void {
    this.keepTheKeyboardOnTheMenuPath(this.menuFocus.focusedKeyPath());
  }

  /**
   * The half of the return above that a caller can hold on to.
   *
   * Split out because a landing decided LATER still has to be aimed from the
   * moment the keyboard was last on a menu item: by the time a navigation
   * answers, the cascade has come down and there is no focused path left to
   * read.
   */
  private keepTheKeyboardOnTheMenuPath(keyPath: string | null): void {
    // The keyboard is somewhere else entirely — outside the menu, or on nobody.
    // Nothing was taken from it, so nothing is owed back.
    if (keyPath === null) return;
    this.menuFocus.scheduleFocus({ keyPath }, this);
  }

  /**
   * Decides where the keyboard lands once the router has answered for the
   * navigation a leaf activation started (AC19, AC4, D15, QA-13).
   *
   * Waiting on the navigation is the point: at key time the destination page is
   * not on screen yet, so the region that is would be the WRONG one — and the
   * one that will be is not in the DOM to focus. Resolved later still, inside
   * the handoff, so what gets focused is the region the navigation produced.
   *
   * **The answer is read, not discarded.** AC19 hands the keyboard to the
   * content region of the page the app changed TO, and a navigation can end
   * without changing the page in three ways this menu really reaches:
   *
   *  - a guard that DENIES without redirecting. Six menu routes are guarded by
   *    one (`ExamCostGuard` and its siblings), which toasts and stays put when
   *    the reader came from inside the app — and none of those routes is
   *    permission-gated out of the tree, so the item is rendered for a reader
   *    who cannot enter it. `navigateByUrl` answers `false`;
   *  - a re-activation of the page already on screen, which the router skips
   *    under the default `onSameUrlNavigation: 'ignore'` and also answers
   *    `false` for;
   *  - a guard or resolver that THROWS, which rejects instead of answering.
   *
   * In all three the reader is looking at the page they were already looking
   * at, so there is no destination region to hand anything to — and the cascade
   * has come down around them all the same. The keyboard therefore goes back to
   * the menu, on the path it was standing on, exactly as it does for the launch
   * that opens another document and leaves this one unchanged. Left to fall
   * through instead, it ends on `document.body`, which AC4 forbids outright.
   */
  private landTheKeyboardOnceTheRouterAnswers(navigated: Promise<boolean>): void {
    // Read HERE, in the same task as the keystroke: the caller takes the
    // cascade down before the router answers, and after that there is no
    // focused menu item left for a path to be read off.
    const whereTheKeyboardStands = this.menuFocus.focusedKeyPath();
    void navigated.then(
      changedPage => {
        if (!changedPage) {
          this.keepTheKeyboardOnTheMenuPath(whereTheKeyboardStands);
          return;
        }
        this.menuFocus.scheduleFocus(
          { resolve: () => document.querySelector<HTMLElement>(CONTENT_REGION_SCOPE) },
          this
        );
      },
      () => this.keepTheKeyboardOnTheMenuPath(whereTheKeyboardStands)
    );
  }

  /**
   * Click outside the sidebar/flyout: closes the flyout (if open) and collapses
   * the sidebar (if expanded). The hamburger button (`.menu-toggle`) is excluded
   * so it does not fight with the header's `toggleSidebar` — clicking it flips
   * the state, and this handler ignores the gesture.
   *
   * A click inside ANY open modal surface is excluded too, and the registry is
   * the oracle rather than a list of overlay classes (#61146). A modal surface
   * used to swallow its own in-box clicks with a `stopPropagation` on its box,
   * which the accessibility lint rules forbid; without that barrier every in-box
   * click reads as "outside the sidebar" here and collapses the bar behind the
   * scrim, reflowing the page under the open modal and moving the very button
   * the closing hands the keyboard back to. Answering from the registry means
   * each converted surface is covered the day it registers, with no per-surface
   * guard to remember.
   *
   * **Any surface, not the topmost one.** With a single modal open the two
   * questions have the same answer, which is why the narrower one held while
   * only one surface at a time could register. Surfaces stack from #61146 Inc 4
   * on — a confirmation over a form, in either topology — and a click inside the
   * surface UNDERNEATH the top is still a click inside a modal.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (this.modalSurfaces.isInsideAnyOpenSurface(target)) return;
    const insideFlyout = !!target.closest('.menu-flyout');
    const insideSidebar = !!target.closest('.sidebar');
    const onHamburger = !!target.closest('.menu-toggle');
    if (insideFlyout || insideSidebar || onHamburger) return;
    if (this.openItem !== null) this.closeFlyout();
    if (this.isOpen) this.requestSidebarClose.emit();
  }

  /**
   * The BAR as a region — wider than the menu above, and deliberately so: the
   * whole `<aside>`, FOOTER INCLUDED, plus the cascade panels rendered outside
   * it.
   *
   * The exact two places `onDocumentClick` calls "inside", because this is the
   * boundary the behaviour was asked for against: the bar stays expanded while
   * the keyboard is in it, and once the keyboard is out it may collapse just as
   * the click outside already collapses it.
   *
   * **Not `ROOT_MENUBAR_SELECTOR`.** The footer is outside the menubar on
   * purpose — it is where `Tab` goes when it leaves the menu (AC7) — so a bar
   * measured by the menubar reads `Tab` from the last root item as a departure
   * while the user is still standing inside the bar, one stop further down.
   */
  private static readonly BAR_REGION_SELECTOR = `.sidebar, ${MENU_PANEL_SCOPE}`;

  /**
   * A pointer press is under way, so the focus landing it is about to produce
   * is the mouse's and none of the rule below's business (D7). The mouse keeps
   * the two expansions it already had — hover, and a click on a parent item —
   * and gains none: clicking a root leaf or a footer link leaves the bar as it
   * found it, exactly as before.
   */
  @HostListener('document:mousedown')
  onDocumentMouseDown(): void {
    this.focusLandingIsAPointerPress = true;
    if (this.pointerPressTimer !== null) clearTimeout(this.pointerPressTimer);
    this.pointerPressTimer = setTimeout(() => {
      this.focusLandingIsAPointerPress = false;
      this.pointerPressTimer = null;
    });
  }

  /**
   * The keyboard has landed somewhere: if that somewhere is inside the bar, the
   * bar is expanded — the counterpart of what hover has done since TASK-59001,
   * for the input device that had none. A collapsed bar walked with the arrows
   * showed icons and no labels, because focus asked for nothing.
   *
   * **Read on `focusin`, never on `focusout`.** Between the blur of one element
   * and the focus of the next, `document.activeElement` is `document.body` —
   * the DOM working, not a departure — and a rule that fired on the way OUT
   * would fire inside that window on every single arrow key. `focusin` is the
   * rest point: it only ever fires once the focus has landed on a real element,
   * which is the same "read membership where the action lands" the Escape gate
   * and the parked-opening landing both already practise.
   *
   * **Restated at every landing, not only at the first.** "The bar is open
   * while the keyboard is in it" is a standing claim, not a one-off transition:
   * anything may collapse the bar underneath a keyboard that never left it (the
   * header hamburger, a click outside, a leaf activation), and the next key then
   * puts it back. The layout answers with an assignment, so restating is free.
   *
   * **Nothing here collapses.** Walking OUT of the bar removes the reason to
   * hold it open; it does not add a new reason to close it. The paths that
   * already close it — the click outside and leaf activation — are untouched,
   * which is also what keeps a `Tab` out of the menu from taking a cascade down
   * with it (QA-11, out of scope).
   */
  @HostListener('document:focusin', ['$event'])
  onFocusLanding(event: FocusEvent): void {
    const landed = event.target;
    if (!(landed instanceof HTMLElement)) return;
    if (this.focusLandingIsAPointerPress) return;
    if (!landed.closest(SidebarComponent.BAR_REGION_SELECTOR)) return;
    this.requestSidebarOpen.emit();
  }

  /**
   * The menu, as a region: the root menubar plus the cascade panels, which are
   * rendered outside `<aside>` to escape its `overflow: hidden` and are menu
   * all the same (D11). The sidebar footer is deliberately left out — it stays
   * tabbable and it is where `Tab` goes when it leaves the menu.
   *
   * The two scopes come from the focus owner, which resolves against the very
   * same regions: an Escape the menu claims and a focus the menu recovers have
   * to agree on where the menu ends.
   */
  private static readonly ROOT_MENUBAR_SELECTOR = MENU_ROOT_SCOPE;
  private static readonly CASCADE_PANEL_SELECTOR = MENU_PANEL_SCOPE;

  /**
   * Who this Escape is addressed to, read once from where the keyboard is and
   * from what put the cascade on screen (AC12, D7, D11). One reading, one
   * table: the outcomes used to be spread over `onEscape` plus three helpers,
   * with the third of them buried in the false branch of a negated predicate
   * and `document.activeElement` consulted three times for the same keypress.
   *
   * - `panel` — the keyboard is inside a cascade panel;
   * - `root` — the keyboard is on a root item;
   * - `orphan-keyboard` — nobody holds the keyboard and the cascade on screen
   *   is the one a key opened. It is the state the incident itself produces,
   *   and it is the menu's press: every arrow handler hangs off a menu
   *   element, so once the focus is orphaned no key reaches the menu any more
   *   and declining here would leave the panel open for good;
   * - `orphan-mouse` — nobody holds the keyboard and the cascade is the
   *   mouse's. Escape dismisses it, because that is what the gesture asks for
   *   and no arrow key can reach the menu to do it instead, but the press is
   *   not the menu's: it is neither consumed nor answered with a focus ring on
   *   an item this user never navigated to;
   * - `not-ours` — the keyboard is on a real element outside the menu, and
   *   that element (or the overlay around it) is the legitimate claimant of
   *   the key. The menu touches nothing: closing the cascade from here is the
   *   side effect C-03/DIV-01 named.
   */
  private escapeAudience(): 'panel' | 'root' | 'orphan-keyboard' | 'orphan-mouse' | 'not-ours' {
    const focused = this.focusedElement();
    if (!focused) return this.flyoutAutofocus ? 'orphan-keyboard' : 'orphan-mouse';
    if (focused.closest(SidebarComponent.ROOT_MENUBAR_SELECTOR)) return 'root';
    return focused.closest(SidebarComponent.CASCADE_PANEL_SELECTOR) ? 'panel' : 'not-ours';
  }

  /**
   * Escape is a document-wide key: this app registers eight
   * `document:keydown.escape` listeners — this menu, the mobile drawer, the
   * header dropdowns, the modal shell, the context menu, the filter drawer and
   * two feature modals — so the menu answers it only when the press is its own
   * (AC12).
   *
   * What `preventDefault()` here does NOT do is stop the other seven: they all
   * hang off the same document event, and only `stopImmediatePropagation`
   * would cut them off. AC16 holds for two concrete reasons — the drawer and
   * the header act unconditionally on their own state, and this menu never
   * calls `stopImmediatePropagation`. No production code in this app reads
   * `defaultPrevented` (grep it: only comments and specs do), so claiming the
   * flag is what protects them would be asserting a mechanism that does not
   * exist.
   *
   * The event is typed as `Event` because Angular resolves the `keydown.escape`
   * pseudo-event to the base type, and nothing here needs a keyboard member:
   * `preventDefault()` is what marks the press as answered.
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // Nothing of the menu's is open, so there is nothing to close and no reason
    // to claim the key — including right after a panel handled its own Escape.
    // An opening the KEYBOARD is still waiting on IS the menu's, though it has
    // not written `openItem` yet: read off that field alone, the press went to
    // whoever else wanted it and the cascade the user had just dismissed
    // appeared right after it (AC15).
    //
    // Whose opening it is has to be part of the question. Hover parks one
    // through the same helper, and answered on "something is pending" the menu
    // consumes a press it owns nothing of, with nothing open — one of the cases
    // AC12 names — and drops the cascade the cursor asked for on its way out.
    if (this.openItem === null && this.pendingOpen?.fromKeyboard !== true) return;
    const audience = this.escapeAudience();
    // Whichever of the four remaining outcomes it is, the cascade goes — and
    // one that has not reached the screen yet goes with it, before the switch
    // decides where the keyboard lands.
    if (audience !== 'not-ours') this.cancelPendingExpandAction();
    switch (audience) {
      // Someone else's press: the menu neither closes nor consumes (AC12, D7).
      case 'not-ours': return;
      // Nobody's press, but the cascade still has to go — quietly (AC12, D7).
      case 'orphan-mouse': this.closeFlyout(); return;
      // The keyboard is already where a return would put it: close and stay
      // put, because the panel may belong to another root item and dragging
      // the user to it is not a return (AC17, D14).
      case 'root': event.preventDefault(); this.closeFlyout(); return;
      // The cascade closes and the root item that anchors it takes the
      // keyboard back (AC3) — including from an orphaned focus, the one state
      // in which no other key could ever reach the menu to close it.
      case 'panel':
      case 'orphan-keyboard': event.preventDefault(); this.onFlyoutClose(); return;
    }
  }

  /** The element holding the keyboard, or null when nobody does. */
  private focusedElement(): HTMLElement | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body) return null;
    return active;
  }

  // ── Loading and mapping ─────────────────────────────────────────────────

  /** Empty granted-set used while permission checks have not resolved yet. */
  private static readonly NO_PERMISSIONS_GRANTED: ReadonlySet<string> = new Set<string>();

  /**
   * Monotonic token per menu emission — a late permission resolution never
   * overwrites the tree of a newer `localizedModules$` emission.
   */
  private menuRenderSeq = 0;

  private loadMenuFromBackend(): void {
    this.menuLoading = true;
    // Use the shared cache from SidebarStateService to avoid re-fetching on
    // every navigation (LayoutComponent is recreated by each feature page).
    this.subscription.add(
      this.sidebarState.localizedModules$.subscribe({
        next: (modules: LocalizedModuleDto[]) => this.renderMenu(modules),
        error: () => {
          // Do not wipe the menu on API failure (e.g. 429) — keep last tree if any.
          this.menuLoading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /**
   * TASK-60341 (AC2): renders the tree immediately with permission-gated items
   * (keys in `SUBMODULE_KEY_TO_PERMISSION`) omitted, then consults
   * `UserAccessService.check` only for the gated keys present in the tree.
   * A gated item is only ever rendered AFTER its check resolves to true — it
   * never flickers in and out. Items outside the map are never checked and
   * render exactly as before. Check errors degrade to hidden (the service
   * already maps any error to `false`).
   */
  private renderMenu(modules: LocalizedModuleDto[]): void {
    const seq = ++this.menuRenderSeq;
    const gatedPermissions = this.collectGatedPermissions(modules);
    // The first pass is PROVISIONAL whenever a second one is coming: its tree
    // is gated to nothing on purpose, which is a statement about what has been
    // answered so far, never about what the user may see.
    this.applyMenu(modules, SidebarComponent.NO_PERMISSIONS_GRANTED, gatedPermissions.length > 0);
    if (gatedPermissions.length === 0) {
      return;
    }
    this.subscription.add(
      forkJoin(gatedPermissions.map(permission => this.userAccess.check(permission))).subscribe(results => {
        if (seq !== this.menuRenderSeq) {
          return; // a newer menu emission superseded this resolution
        }
        // Applied even when NOTHING was granted. The provisional pass no longer
        // decides anything about the open cascade or the keyboard, so the final
        // pass is the only one that can — skipping it on an empty granted set
        // would leave a cascade open on a branch the checks just denied.
        this.applyMenu(modules, new Set(gatedPermissions.filter((_, index) => results[index])));
      })
    );
  }

  /**
   * Applies `modules` gated by `granted` as the tree on screen.
   *
   * `provisional` says this is the first of the two passes `renderMenu` makes
   * and the second is still coming. That first tree omits every gated item
   * because no check has answered yet — a module whose children are all gated
   * is missing from it altogether — so reading it as "the branch did not
   * survive" acts on an answer nobody gave. The state it would destroy is not
   * recoverable: `closeFlyout()` clears `openItem`, and `rebindOpenCascade`
   * returns at `openItem === null` on the way in, so the final pass finds
   * nothing to re-point and the cascade never comes back (AC9). Nothing is
   * rendered between the two passes — `applyMenu` only marks for check — so
   * deferring both decisions to the final pass costs no visible frame.
   */
  private applyMenu(
    modules: LocalizedModuleDto[],
    granted: ReadonlySet<string>,
    provisional = false
  ): void {
    // Read BEFORE the tree is replaced: the key path is derived from the menu
    // that is on screen, and the assignment below is what takes it away.
    const focusedKeyPath = this.menuFocus.focusedKeyPath();
    this.menuItems = this.mapModulesToMenuItems(modules, granted);
    this.activePath = this.computeActivePath(this.menuItems, this.router.url.split('?')[0]);
    this.menuLoading = false;
    this.rebindOpenCascade(provisional);
    this.cdr.markForCheck();
    if (provisional) return;
    this.dropPendingOpeningOfAPrunedBranch();
    if (focusedKeyPath !== null && !this.keyPathStillRendered(focusedKeyPath)) {
      // Only when the item actually went away. A re-render is the one way the
      // focused item can vanish with no gesture behind it (D13), so the
      // recovery has to exist — but scheduling it unconditionally made every
      // re-render supersede whatever handoff was already in flight, and the
      // menu's focus owner keeps exactly one. A permission check resolving
      // while a panel opened by ArrowRight was still handing the keyboard over
      // then cancelled that handoff and "recovered" the root item the key had
      // been pressed on: panel open, keyboard outside it (AC5). Recovering a
      // focus that was never lost is invented work, and it costs the real move.
      //
      // Deferred, because the tree just declared has not been rendered yet —
      // the target does not exist until it has (AC6, AC9).
      this.menuFocus.scheduleFocus({ keyPath: focusedKeyPath }, this);
    }
  }

  /**
   * Drops a keyboard opening still waiting for the sidebar when the branch it
   * would put on screen did not survive the tree that was just applied.
   *
   * The pending half of what `rebindOpenCascade` does to a cascade already on
   * screen, decided by the same predicate, in the same pass: those two read
   * `openItem`, and a wait that has not landed has not written it, so the
   * reconstruction had no way to see it at all. Left alive, a keyboard one goes
   * on answering `Esc` for the rest of the expansion with nothing of the menu's
   * behind it (AC12), and the opening it still promises has to be turned away
   * again when it lands.
   *
   * Whose opening it is does not come into it. Read off the keyboard's wait
   * alone, a hover on a collapsed sidebar walked straight past this: the branch
   * was pruned mid-expansion and the cascade landed anyway, anchored on an `<a>`
   * the re-render had detached — which puts the panel at 0,0 — showing items no
   * longer in the menu (AC8). The gesture decides who may CLAIM a key, never
   * whether the branch underneath still exists.
   *
   * A branch that DID survive is left alone: the user asked for that cascade
   * and the tree still has it. Dropping it here on every emission would make a
   * permission check landing mid-expansion swallow the key the user pressed.
   *
   * **Never on the provisional pass** — `applyMenu` says why: that tree is
   * gated to nothing, a branch missing from it means "not answered yet", and
   * this is exactly the user state it may not destroy.
   */
  private dropPendingOpeningOfAPrunedBranch(): void {
    const pending = this.pendingOpen;
    if (pending === null || findOpenableChild(this.menuItems, pending.key)) return;
    this.cancelPendingExpandAction();
  }

  /**
   * Whether the item `keyPath` names is still on screen after the tree that was
   * just applied.
   *
   * Walked over the DATA, not the DOM: `applyMenu` runs before the change
   * detection that publishes the new tree, so the DOM here is still the
   * previous one and would answer for a menu that no longer exists.
   *
   * The walk does not RESTATE the rule that decides what stays mounted — it
   * calls it. Every segment above the deepest has to be able to host the level
   * below it, which is `findOpenableChild`, the same predicate
   * `rebindOpenCascade` here and `rebindOpenChild` in the panel re-point by; the
   * deepest segment only has to exist. `trackBy` on both `*ngFor`s then reuses
   * the very same element for a key that survived, so a path whose every
   * segment survives is still rendered, on the same nodes, still holding the
   * keyboard. Predicting that rule instead of sharing it is what makes a change
   * to the panel's mounting silently unteach this walk.
   */
  private keyPathStillRendered(keyPath: string): boolean {
    const segments = menuKeyPathSegments(keyPath);
    const deepest = segments.length - 1;
    let level: MenuItem[] = this.menuItems;
    for (let index = 0; index <= deepest; index++) {
      const found = index === deepest
        ? findByKey(level, segments[index])
        : findOpenableChild(level, segments[index]);
      if (!found) return false;
      level = found.children ?? [];
    }
    return true;
  }

  /**
   * Re-points the open cascade at the tree that was just applied.
   *
   * `openItem` is the object the user opened, and every emission of the menu
   * rebuilds the tree from the DTOs — so the panel went on rendering
   * `openItem.children` of the PREVIOUS emission, showing items a permission
   * check had just denied and hiding ones it had just granted. Matched by
   * stable key, never by reference (cause C-04); a branch that did not survive
   * takes its cascade down with it.
   *
   * **The invariant this method exists to hold.** `openItem` is a `MenuItem`
   * reference, and it is read as "the item of the tree currently on screen" —
   * the `ArrowRight` branch of `onRootKeydown` opens `openItem.children` off it.
   * Every emission of the menu rebuilds those objects, so re-pointing here is
   * what keeps that reading the tree the user is looking at. Holding a key
   * instead of the object would make the invariant structural rather than
   * maintained — a larger refactor, deliberately not taken in this increment.
   *
   * The two places that used to COMPARE against it by reference — the
   * `openFlyout` of `onRootMouseEnter` and `toggleFlyout`'s "second click on an
   * open cascade" — no longer do: both ask by stable key, and both re-resolve
   * the item against the tree on screen before they open it. Their wait can
   * outlast an emission, which is a window this re-point cannot reach at all
   * (it reads `openItem`, and a wait that has not landed has not written it).
   *
   * On the PROVISIONAL pass the cascade is never taken down: see `applyMenu`.
   */
  private rebindOpenCascade(provisional = false): void {
    if (this.openItem === null) return;
    const rebound = findOpenableChild(this.menuItems, menuItemKey(this.openItem));
    if (rebound) {
      this.openItem = rebound;
      return;
    }
    // The branch did not survive the re-render, so neither does its cascade —
    // unless the tree that says so is the provisional one, which cannot: gated
    // to nothing, it omits branches the final pass brings back, and
    // `closeFlyout()` is not something the final pass can undo.
    if (!provisional) this.closeFlyout();
  }

  /** True when any submodule (at any depth) is permission-gated via the map. */
  private hasGatedDescendant(submodules: LocalizedSubmoduleDto[] | null | undefined): boolean {
    return (submodules ?? []).some(
      sub => !!SUBMODULE_KEY_TO_PERMISSION[sub.key] || this.hasGatedDescendant(sub.children)
    );
  }

  /** Distinct permission keys required by gated submodules present in the tree. */
  private collectGatedPermissions(modules: LocalizedModuleDto[]): string[] {
    const permissions = new Set<string>();
    const visit = (submodules: LocalizedSubmoduleDto[] | null | undefined): void => {
      for (const sub of submodules ?? []) {
        const permission = SUBMODULE_KEY_TO_PERMISSION[sub.key];
        if (permission) {
          permissions.add(permission);
        }
        visit(sub.children);
      }
    };
    for (const module of modules) {
      visit(module.submodules);
    }
    return [...permissions];
  }

  private mapModulesToMenuItems(modules: LocalizedModuleDto[], granted: ReadonlySet<string>): MenuItem[] {
    // AC12: remove Módulo Interlac, Laudos Web, Confere e Integre do menu lateral.
    return modules
      .filter(module => !isRemovedMenuModule(module.route ?? undefined, module.key))
      .map(module => this.mapModuleToMenuItem(module, granted))
      .filter((item): item is MenuItem => item !== null);
  }

  private mapModuleToMenuItem(module: LocalizedModuleDto, granted: ReadonlySet<string>): MenuItem | null {
    const iconSvg = backendIconToLucide(module.icon ?? undefined);
    const hasSubmodules = module.submodules && module.submodules.length > 0;

    // Some modules ship as a parent + single submodule pair on the backend
    // but should render as a top-level leaf — same UX as Início. The shared
    // override map decides those, but only while the module has at most one
    // submodule: with a second one, collapsing the group into a leaf would
    // hide every item but the override target from the desktop sidebar.
    const overrideRoute = getDefaultRouteForModuleName(module.name);
    if (overrideRoute && (module.submodules?.length ?? 0) <= 1) {
      return {
        id: `module:${module.key}`,
        label: module.name,
        iconSvg,
        key: module.key,
        route: overrideRoute,
      };
    }

    if (hasSubmodules) {
      // Filhos de Workspace Laboratorial são jump-offs para o AVD enquanto
      // as páginas reais não existem no SPA — pular o mapeamento de rota
      // mantém a rota crua (ex.: /atendimento), que cai em isSpaInternalRoute=false
      // e abre o cliente Windows, mesmo padrão de Operações Laboratoriais (/autolac).
      const skipRouteMapping = module.key === 'workspace_laboratorial';
      const children = this.mapSubmodulesToMenuItems(module.submodules, skipRouteMapping, granted);
      // TASK-60341 (AC2): a group emptied by permission gating disappears too
      // (e.g. Cadastros when the user cannot see any of its cadastros).
      // Groups without gated descendants are never pruned — zero regression.
      if (children.length === 0 && this.hasGatedDescendant(module.submodules)) {
        return null;
      }
      return {
        id: `module:${module.key}`,
        label: module.name,
        iconSvg,
        key: module.key,
        children
      };
    }

    const routeFromBackend = module.route ? backendRouteToAngular(module.route) || module.route : undefined;
    const route = routeFromBackend;
    return {
      id: `module:${module.key}`,
      label: module.name,
      iconSvg,
      key: module.key,
      route
    };
  }

  /**
   * Converts the LocalizedSubmoduleDto tree from the backend into recursive MenuItems.
   * Folders (no `route`) become nodes with `children`; leaves get a `route`
   * resolved via `backendRouteToAngular`. Submodules with the same route are
   * deduplicated (preserves compatibility with legacy keys) — folders skip
   * dedupe because they have no route.
   *
   * @param skipRouteMapping When true, preserves the raw backend route (skips
   * `backendRouteToAngular`). Used pelos filhos de Workspace Laboratorial, cujas
   * páginas reais ainda não existem no SPA — manter a rota crua faz `isSpaInternalRoute`
   * retornar false e o leaf abre o AVD em vez de tentar navegar.
   */
  private mapSubmodulesToMenuItems(
    submodules: LocalizedSubmoduleDto[],
    skipRouteMapping = false,
    granted: ReadonlySet<string> = SidebarComponent.NO_PERMISSIONS_GRANTED
  ): MenuItem[] {
    const seenRoute = new Set<string>();
    const items: MenuItem[] = [];
    for (const sub of submodules) {
      // AC12: remove Módulo Interlac, Laudos Web, Confere e Integre do menu lateral.
      if (isRemovedMenuModule(sub.route ?? undefined, sub.key)) continue;
      // TASK-60341 (AC2): gated submodules only render after their permission
      // check resolved to true. Keys outside the map are never gated.
      const requiredPermission = SUBMODULE_KEY_TO_PERMISSION[sub.key];
      if (requiredPermission && !granted.has(requiredPermission)) continue;
      const isFolder = !sub.route;
      const angularRoute = isFolder
        ? undefined
        : (skipRouteMapping ? sub.route! : (backendRouteToAngular(sub.route!) || sub.route!));

      if (angularRoute) {
        const key = angularRoute.toLowerCase();
        if (seenRoute.has(key)) continue;
        seenRoute.add(key);
      }

      const nestedChildren = sub.children && sub.children.length > 0
        ? this.mapSubmodulesToMenuItems(sub.children, skipRouteMapping, granted)
        : undefined;

      items.push({
        id: `submodule:${sub.key}`,
        label: sub.name,
        route: angularRoute,
        iconSvg: backendIconToLucide(sub.icon ?? undefined),
        key: sub.key,
        externalLaunchUrlTemplate: sub.externalLaunchUrlTemplate ?? undefined,
        children: nestedChildren
      });
    }
    return items;
  }

  /**
   * Active path: set of IDs of every item (root and descendants) that are
   * ancestors of the current route. Used to highlight the open branch.
   *
   * "Módulo X" shortcuts are skipped — their route is just a jump-off into
   * the module and matching it as a leaf would wrongly highlight the
   * shortcut (e.g. "Módulo Atendimento" lighting up while the user is on
   * "Fila de Coleta", since both share the /attendance prefix).
   */
  private computeActivePath(items: MenuItem[], currentUrl: string): ReadonlySet<string | number> {
    const path = new Set<string | number>();
    const traverse = (nodes: MenuItem[]): boolean => {
      for (const node of nodes) {
        const key = node.id ?? node.label;
        const isShortcut = isModuleShortcutLabel(node.labelKey ?? node.label);
        const isLeafActive = !isShortcut
          && !!node.route
          && (currentUrl === node.route || currentUrl.startsWith(node.route + '/'));
        if (isLeafActive) {
          path.add(key);
          return true;
        }
        if (node.children && traverse(node.children)) {
          path.add(key);
          return true;
        }
      }
      return false;
    };
    traverse(items);
    return path;
  }

  /**
   * Pareia o agente local (AutolacPrinterLocal) com o operador ANTES de abrir o
   * legado no AVD: descobre a porta (/ping + validação de `product`), e dispara
   * o /pair com o Entra ObjectId do operador. É **não-bloqueante** — em qualquer
   * falha apenas avisa o usuário e segue; o legado abre mesmo assim (a impressão
   * pode não funcionar, e ele pode reabrir). Contrato:
   * docs/autolac-printer-avd/web-pair-local/integracao-autolac-web-agente-local.md (§7).
   */
  private async pairLocalAgentForAvd(quick: boolean): Promise<boolean> {
    const operatorObjectId = this.getOperatorObjectId();
    if (!operatorObjectId) {
      this.notificationService.error(this.i18n.translate('layout.sidebar.toast.errorOperator'));
      return false;
    }

    const port = await this.agentPrintService.findAgentPort(quick);
    if (!port) {
      if (!quick) {
        this.notificationService.error(this.i18n.translate('layout.sidebar.toast.printAgentNotFound'));
      }
      return false;
    }

    const result = await this.agentPrintService.pair(port, operatorObjectId);
    if (!result.ok) {
      if (result.status === 502) {
        this.notificationService.error(this.i18n.translate('layout.sidebar.toast.errorRegisterPrint'));
      } else {
        this.notificationService.error(this.i18n.translate('layout.sidebar.toast.errorPrintAgentGeneric'));
      }
    }

    return true;
  }

  /**
   * ObjectId (OID) do Entra do operador. Confirmado (2026-06-24) como o valor de
   * `user.id` do usuário salvo em sessão. É o MESMO valor que o AutolacPrinterAvd
   * resolve via WAM — precisa casar para a printer list chegar à sessão AVD certa.
   * Retorna null se não houver usuário em sessão (aí o pareamento é pulado).
   */
  private getOperatorObjectId(): string | null {
    const savedUser = sessionStorage.getItem(environment.auth.userStorageKey);
    if (!savedUser) {
      return null;
    }
    try {
      const user = JSON.parse(savedUser);
      const oid = user?.id;
      return (typeof oid === 'string' && oid.length > 0) ? oid : null;
    } catch {
      return null;
    }
  }

  logout(event: Event): void {
    event.preventDefault();
    this.authService.logout();
  }
}
